import React, { useEffect, useRef, useState } from "react";
import { useLocation } from 'react-router-dom';

import { useTheme } from '../ThemeContext';
import Footer from './Footer.jsx';

import KeyboardViewer from "./Models/Keyboard.jsx";

const RevealOnScroll = ({ children }) => {
    const [isVisible, setIsVisible] = useState(false);
    const ref = useRef(null);
  
    useEffect(() => {
      let observerRefValue = null;
      const scrollObserver = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
              setIsVisible(true);
              scrollObserver.unobserve(entry.target);
          }
        },
      );
      
      scrollObserver.observe(ref.current);
      observerRefValue = ref.current;
  
      return () => {
        if (observerRefValue) {
          scrollObserver.unobserve(observerRefValue);
        }
      };
    }, []);
    
    const classes = `transition-opacity duration-1500 ${
      isVisible ? 'opacity-100' : 'opacity-0'
    }`;
  
    return (
      <div ref={ref} className={classes}>
        {children}
      </div>
    );
};

const Home = () => {
    const location = useLocation();
    const { isDarkMode } = useTheme();

    useEffect(() => {
        // Check if the location has an anchor (e.g., /#mailing)
        if (location.hash) {
        const targetElement = document.getElementById(location.hash.substring(1));
        if (targetElement) {
            // Scroll to the target element with smooth behavior
            targetElement.scrollIntoView({ behavior: "smooth", block: "center"});
        }
        }
    }, [location]);

    return (
        <div className={isDarkMode ? 'min-h-screen bg-slate-800 text-amber-100' : 'min-h-screen bg-amber-100 text-slate-800'}>

            {/* Header */}
            <header 
                className="min-h-screen bg-cover bg-center bg-fixed flex items-stretch justify-center transition-all duration-500 ease-in-out"
            >   

                <div className="absolute inset-0">
                    <KeyboardViewer scale="0.1" modelPath={"./renders/KeyboardLeo.glb"} />
                </div>
                
                <RevealOnScroll>
                    <div className="flex flex-col items-center min-h-screen min-w-screen relative z-10 my-40">
                        <div className="text-center md:text-8xl text-5xl font-semibold">
                            <h1 className="">Keebs at Berkeley</h1>
                        </div>
                        <div className="text-center mt-2 text-2xl font-medium">
                            <p className="">A mechanical keyboard community :)</p>
                        </div>
                    </div>
                </RevealOnScroll>
                
                {/* Scroll down arrow */}
                <a href="#about" className="absolute bottom-10 animate-bounce cursor-pointer z-5">
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" className="w-8 h-8">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 13.5 12 21m0 0-7.5-7.5M12 21V3" />
                    </svg>
                </a>
            </header>

            {/* Main Content */}
            <main className="container mx-auto p-10 transition-all duration-500 ease-in-out">

                <RevealOnScroll>
                    {/* Neumorphic Design */}
                    <div id="about" className="transition-all duration-500 ease-in-out mt-10 p-10 shadow-[0.625rem_0.625rem_0.875rem_0_rgb(225,226,228),-0.5rem_-0.5rem_1.125rem_0_rgb(255,255,255)] rounded-lg flex flex-wrap justify-center gap-5 [&>*]:w-[8rem] [&>*]:h-[8rem] [&>*]:py-4 [&>*:hover]:shadow-[0.625rem_0.625rem_0.875rem_0_rgb(225,226,228),-0.5rem_-0.5rem_1.125rem_0_rgb(255,255,255)] 
                    [&>*>div]:w-16 [&>*>div]:h-16 [&>*>div]:rounded-full [&>*>div]:text-white [&>*>div]:flex [&>*>div]:items-center [&>*>div]:justify-center [&>*>div]:mx-auto [&>*>h2]:text-[0.8rem] [&>*>h2]:mt-4 [&>*>h2]:text-center">
                        <a href="/calendar">
                            <div className="bg-rose-500 hover:bg-rose-600">
                                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" className="w-8 h-8">
                                    <path stroke-linecap="round" stroke-linejoin="round" d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 0 1 2.25-2.25h13.5A2.25 2.25 0 0 1 21 7.5v11.25m-18 0A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75m-18 0v-7.5A2.25 2.25 0 0 1 5.25 9h13.5A2.25 2.25 0 0 1 21 11.25v7.5" />
                                </svg>
                            </div>
                            <h2 className="text-center text-2xl font-semibold">CALENDAR</h2>
                        </a>
                        <a href="/#mailing" >
                            <div className="bg-blue-500 hover:bg-blue-600">
                                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" className="w-8 h-8">
                                    <path stroke-linecap="round" stroke-linejoin="round" d="M21.75 6.75v10.5a2.25 2.25 0 0 1-2.25 2.25h-15a2.25 2.25 0 0 1-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0 0 19.5 4.5h-15a2.25 2.25 0 0 0-2.25 2.25m19.5 0v.243a2.25 2.25 0 0 1-1.07 1.916l-7.5 4.615a2.25 2.25 0 0 1-2.36 0L3.32 8.91a2.25 2.25 0 0 1-1.07-1.916V6.75" />
                                </svg>
                            </div>
                            <h2 className="text-center text-2xl font-semibold">MAILING LIST</h2>
                        </a>
                        <a href="/links">
                            <div className="bg-violet-500 hover:bg-violet-600">
                                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" className="w-8 h-8">
                                    <path stroke-linecap="round" stroke-linejoin="round" d="M13.19 8.688a4.5 4.5 0 0 1 1.242 7.244l-4.5 4.5a4.5 4.5 0 0 1-6.364-6.364l1.757-1.757m13.35-.622 1.757-1.757a4.5 4.5 0 0 0-6.364-6.364l-4.5 4.5a4.5 4.5 0 0 0 1.242 7.244" />
                                </svg>
                            </div>
                            <h2 className="text-center text-2xl font-semibold">LINKS</h2>
                        </a>
                    </div>
                </RevealOnScroll>

                {/* ABOUT US */}
                <div className="flex flex-col my-4 md:p-8">
                    <RevealOnScroll>
                        <h3 className="md:text-xl text-center font-medium mb-4">ABOUT US</h3>
                    </RevealOnScroll>
                    <div className="md:px-10">
                        <RevealOnScroll>
                            <h1 className="text-center font-bold md:text-6xl text-4xl py-2">A community of keeb enjoyers!</h1>
                        </RevealOnScroll>
                        <RevealOnScroll>
                            <p className="text-left md:text-center md:text-2xl text-xl mt-4 lg:px-40 md:px-20">
                                Founded in 2022, we are <b className="font-semibold">Keebs at Berkeley</b>, UC Berkeley's one and only mechanical keyboard club. Join us as we develop our club and share our interests and passions of 
                                restoring, designing, building, and engineering all sorts of keyboards!
                            </p>
                        </RevealOnScroll>
                    </div>
                    
                </div>

                {/* Mailing List */}
                <RevealOnScroll>
                <div className="md:grid md:grid-cols-2 md:gap-4 md:p-8">
                    <div className="flex flex-col items-center justify-center">
                        <h2 className="text-center font-bold md:text-6xl text-4xl py-2">Contact Us</h2>
                        <p className="text-left md:text-2xl text-xl mt-4">
                            We are working diligently to make Keebs at Berkeley a warm environment for newcomers and veteran keeb enthusiasts alike. 
                            If you have suggestions for us or would like to contact us for any questions, comments, or concerns, please email us at keebs.berkeley@gmail.com.
                        </p>
                    </div>
                    <div id="mailing" className="mt-10 md:p-10 p-2 shadow-[0.625rem_0.625rem_0.875rem_0_rgb(225,226,228),-0.5rem_-0.5rem_1.125rem_0_rgb(255,255,255)] rounded-lg">
                        <iframe title="Keebs Mailing List" src="https://airtable.com/embed/app5qbnlyWE6YsxMm/paghxCUvWxUwNzIUM/form" frameborder="0" onmousewheel="" width="100%" height="533" className="bg-transparent border-1;"></iframe>
                    </div>
                </div>
                </RevealOnScroll>
                <div className="flex items-center justify-center">
                    <div className="mt-10 p-5 shadow-[0.625rem_0.625rem_0.875rem_0_rgb(225,226,228),-0.5rem_-0.5rem_1.125rem_0_rgb(255,255,255)] rounded-full">
                        <p className="text-xs">Designed by Cameron :)</p>
                    </div>
                </div>
            </main>
            <Footer />
        </div>
    );
};

export default Home;
